import React, { useState, useMemo, useRef, useCallback, useEffect } from "react";
import { PlusIcon, CheckIcon, ChevronIcon, EditIcon, TrashIcon } from "../icons";
import { fmtMonth } from "../utils/helpers";
import S from "../styles";

const API_URL = process.env.REACT_APP_API_URL || "https://api.nycmanufacturing.com";

const MONTH_NAMES_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// Design Shop: gross revenue + net (after platform fees) per marketplace.
// Gross fields drive Total Revenue; net fields drive Total Net and profit.
const DS_REVENUE_FIELDS = [
  { key: "amazonRevenue", label: "Amazon Revenue", gross: true, netKey: "amazonNet" },
  { key: "amazonNet", label: "Amazon Net", net: true },
  { key: "etsyRevenue", label: "Etsy Revenue", gross: true, netKey: "etsyNet" },
  { key: "etsyNet", label: "Etsy Net", net: true },
  { key: "ttShopRevenue", label: "TikTok Shop Revenue", gross: true, netKey: "ttShopNet" },
  { key: "ttShopNet", label: "TikTok Shop Net", net: true },
  // Flat payout for recording our packing process — no platform fees, so the
  // one number counts as both gross revenue and net.
  { key: "shiftPayout", label: "Shift Payout", gross: true, net: true, netKey: "shiftPayout" },
  // Work we do for outside customers. Entered as one number (job costs go in
  // Expenses), so it also counts as both gross revenue and net.
  { key: "externalJobs", label: "External Jobs", gross: true, net: true, netKey: "externalJobs" },
];
const DS_GROSS_FIELDS = DS_REVENUE_FIELDS.filter((f) => f.gross);
const DS_NET_FIELDS = DS_REVENUE_FIELDS.filter((f) => f.net);
// Vibrato Vault keeps the legacy layout until its own overhaul.
const VV_REVENUE_FIELDS = [
  { key: "sales", label: "Sales" },
  { key: "marketing", label: "Marketing" },
  { key: "shipping", label: "Shipping" },
  { key: "etsyNet", label: "Etsy Net" },
  { key: "amazonNet", label: "Amazon Net" },
  { key: "ttShopNet", label: "TT Shop Net" },
];

const FIELD_COLORS = {
  sales: "#0033FF", marketing: "#ef4444", shipping: "#f59e0b",
  etsyNet: "#f97316", amazonNet: "#3b82f6", ttShopNet: "#10b981",
  etsyRevenue: "#fb923c", amazonRevenue: "#60a5fa", ttShopRevenue: "#2dd4bf",
  shiftPayout: "#a78bfa", externalJobs: "#ec4899",
};

const OWNER_COLORS = ["#0033FF", "#3b82f6", "#a78bfa", "#f59e0b", "#34d399", "#f97316", "#ef4444", "#10b981", "#ec4899", "#6366f1"];

// ─── Line Chart ───────────────────────────────────────
function RevenueLineChart({ data, height = 240 }) {
  const [hoverIdx, setHoverIdx] = useState(null);
  const containerRef = useRef(null);
  const allVals = data.flatMap((d) => [d.total, d.profit || 0]);
  const maxVal = Math.max(...allVals, 1);
  const minVal = Math.min(...allVals, 0);
  const range = maxVal - minVal || 1;
  const svgW = 600, svgH = height;
  const padT = 16, padB = 28, padL = 54, padR = 16;
  const chartW = svgW - padL - padR;
  const chartH = svgH - padT - padB;

  const getY = (val) => padT + chartH - ((val - minVal) / range) * chartH;
  const getX = (i) => padL + (i / Math.max(data.length - 1, 1)) * chartW;
  const fmtAxis = (v) => v >= 1000 ? `$${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : v <= -1000 ? `-$${(Math.abs(v) / 1000).toFixed(Math.abs(v) >= 10000 ? 0 : 1)}k` : `$${v}`;
  const linePath = data.map((d, i) => `${i === 0 ? "M" : "L"}${getX(i)},${getY(d.total)}`).join(" ");
  const areaPath = linePath + ` L${getX(data.length - 1)},${padT + chartH} L${getX(0)},${padT + chartH} Z`;
  const hasProfit = data.some((d) => d.profit !== undefined);
  const profitPath = hasProfit ? data.map((d, i) => `${i === 0 ? "M" : "L"}${getX(i)},${getY(d.profit || 0)}`).join(" ") : "";
  const profitArea = hasProfit ? profitPath + ` L${getX(data.length - 1)},${padT + chartH} L${getX(0)},${padT + chartH} Z` : "";

  const handleMouseMove = useCallback((e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const svgX = ((e.clientX - rect.left) / rect.width) * svgW;
    const idx = Math.round(((svgX - padL) / chartW) * (data.length - 1));
    setHoverIdx(Math.max(0, Math.min(data.length - 1, idx)));
  }, [data.length, chartW]);

  const gridSteps = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div ref={containerRef} style={{ position: "relative" }} onMouseMove={handleMouseMove} onMouseLeave={() => setHoverIdx(null)}>
      <svg viewBox={`0 0 ${svgW} ${svgH}`} width="100%" height="100%" style={{ display: "block" }}>
        {gridSteps.map((f) => {
          const y = padT + chartH - f * chartH;
          const val = minVal + f * range;
          return (<g key={f}><line x1={padL} y1={y} x2={svgW - padR} y2={y} stroke="#0A0A0F" strokeWidth="1" /><text x={padL - 8} y={y + 4} textAnchor="end" fill="rgba(10,10,15,0.58)" fontSize="10" fontFamily="JetBrains Mono, monospace">{fmtAxis(Math.round(val))}</text></g>);
        })}
        <path d={areaPath} fill="rgba(0,51,255,0.10)" />
        <path d={linePath} fill="none" stroke="#0033FF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {hasProfit && <path d={profitArea} fill="#34d39918" />}
        {hasProfit && <path d={profitPath} fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6 3" />}
        {data.map((d, i) => (<circle key={i} cx={getX(i)} cy={getY(d.total)} r={hoverIdx === i ? 5 : 3} fill={hoverIdx === i ? "#fff" : "#0033FF"} stroke="#0033FF" strokeWidth="2" />))}
        {hasProfit && data.map((d, i) => (<circle key={`p${i}`} cx={getX(i)} cy={getY(d.profit || 0)} r={hoverIdx === i ? 4 : 2.5} fill={hoverIdx === i ? "#fff" : "#34d399"} stroke="#34d399" strokeWidth="1.5" />))}
        {hoverIdx !== null && <line x1={getX(hoverIdx)} y1={padT} x2={getX(hoverIdx)} y2={padT + chartH} stroke="rgba(10,10,15,0.58)" strokeWidth="1" strokeDasharray="4 4" />}
        {data.map((d, i) => (<text key={i} x={getX(i)} y={svgH - 6} textAnchor="middle" fill={hoverIdx === i ? "#0A0A0F" : "rgba(10,10,15,0.58)"} fontSize="10" fontWeight="600" fontFamily="Archivo, sans-serif">{d.label}</text>))}
      </svg>
      {hoverIdx !== null && (
        <div style={{ position: "absolute", left: `${(getX(hoverIdx) / svgW) * 100}%`, top: `${(getY(data[hoverIdx].total) / svgH) * 100 - 12}%`, transform: "translateX(-50%) translateY(-100%)", background: "#FFFFFF", border: "1px solid #0033FF", borderRadius: 0, padding: "8px 12px", fontSize: 13, fontFamily: "'JetBrains Mono', monospace", whiteSpace: "nowrap", pointerEvents: "none", zIndex: 10, boxShadow: "0 4px 16px rgba(0,0,0,.4)" }}>
          <div style={{ fontSize: 12, color: "#0A0A0F", fontWeight: 600, marginBottom: 4, fontFamily: "'Archivo', sans-serif" }}>{data[hoverIdx].label}</div>
          <div style={{ color: "#0033FF" }}>Revenue: ${data[hoverIdx].total.toLocaleString()}</div>
          {hasProfit && <div style={{ color: (data[hoverIdx].profit || 0) >= 0 ? "#34d399" : "#ef4444" }}>Profit: ${(data[hoverIdx].profit || 0).toLocaleString()}</div>}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────
export default function StoreRevenuePage({ storeName, store }) {
  const isDS = store === "designshop";
  const REVENUE_FIELDS = isDS ? DS_REVENUE_FIELDS : VV_REVENUE_FIELDS;
  const GROSS_FIELDS = isDS ? DS_GROSS_FIELDS : VV_REVENUE_FIELDS;
  const NET_FIELDS = isDS ? DS_NET_FIELDS : [];
  const emptyRow = { month: "", ...Object.fromEntries(REVENUE_FIELDS.map((f) => [f.key, ""])) };

  const [entries, setEntries] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [addingMonth, setAddingMonth] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [newRow, setNewRow] = useState(emptyRow);
  const [collapsed, setCollapsed] = useState({});
  const [splits, setSplits] = useState([]);
  const fileInputRef = useRef(null);

  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));

  // Fetch from API
  const fetchRevenue = useCallback(() => {
    fetch(`${API_URL}/api/store-revenue?store=${store}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setEntries(data); })
      .catch(() => {});
  }, [store]);

  const fetchExpenses = useCallback(() => {
    fetch(`${API_URL}/api/store-expenses?store=${store}`)
      .then((r) => r.json())
      .then((data) => { if (Array.isArray(data)) setExpenses(data); })
      .catch(() => {});
  }, [store]);

  const fetchSplits = useCallback(() => {
    fetch(`${API_URL}/api/profit-splits?store=${store}`)
      .then((r) => r.json())
      .then((d) => { if (Array.isArray(d.splits)) setSplits(d.splits); })
      .catch(() => {});
  }, [store]);

  useEffect(() => { fetchRevenue(); fetchExpenses(); fetchSplits(); }, [fetchRevenue, fetchExpenses, fetchSplits]);

  const yearEntries = useMemo(() => entries.filter((e) => e.month && e.month.startsWith(String(selectedYear))), [entries, selectedYear]);

  // DS: Total Revenue = gross fields only; Total Net = net fields (profit basis).
  // VV: everything sums into one total (legacy behavior).
  const getTotal = (row) => GROSS_FIELDS.reduce((s, f) => s + (parseFloat(row[f.key]) || 0), 0);
  const getNet = (row) => (isDS ? NET_FIELDS.reduce((s, f) => s + (parseFloat(row[f.key]) || 0), 0) : getTotal(row));

  // Group expenses by month
  const monthlyExpenses = useMemo(() => {
    const map = {};
    expenses.forEach((e) => {
      if (!e.date) return;
      const mk = e.date.slice(0, 7);
      map[mk] = (map[mk] || 0) + (parseFloat(e.cost) || 0);
    });
    return map;
  }, [expenses]);

  // Monthly chart data
  const monthlyData = useMemo(() => {
    return MONTH_NAMES_SHORT.map((label, i) => {
      const mk = `${selectedYear}-${String(i + 1).padStart(2, "0")}`;
      const entry = yearEntries.find((e) => e.month === mk);
      const total = entry ? getTotal(entry) : 0;
      const net = entry ? getNet(entry) : 0;
      const expAmt = monthlyExpenses[mk] || 0;
      return { label, month: mk, total, expenses: expAmt, profit: net - expAmt };
    });
  }, [yearEntries, selectedYear, monthlyExpenses]); // eslint-disable-line react-hooks/exhaustive-deps

  const yearTotal = yearEntries.reduce((s, e) => s + getTotal(e), 0);
  const yearNet = yearEntries.reduce((s, e) => s + getNet(e), 0);
  const yearExpenseTotal = useMemo(() => {
    return Object.entries(monthlyExpenses)
      .filter(([mk]) => mk.startsWith(String(selectedYear)))
      .reduce((s, [, v]) => s + v, 0);
  }, [monthlyExpenses, selectedYear]);
  const yearProfit = yearNet - yearExpenseTotal;
  const yearMargin = yearTotal > 0 ? (yearProfit / yearTotal) * 100 : 0;
  const yearByField = useMemo(() => {
    const result = {};
    REVENUE_FIELDS.forEach((f) => { result[f.key] = yearEntries.reduce((s, e) => s + (parseFloat(e[f.key]) || 0), 0); });
    return result;
  }, [yearEntries, REVENUE_FIELDS]);

  // Grouped by month
  const grouped = useMemo(() => {
    const sorted = [...yearEntries].sort((a, b) => b.month.localeCompare(a.month));
    return sorted;
  }, [yearEntries]);

  const saveNew = () => {
    if (!newRow.month) return;
    fetch(`${API_URL}/api/store-revenue`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ store, ...newRow }),
    })
      .then((r) => r.json())
      .then(() => fetchRevenue())
      .catch(() => {});
    setAddingMonth(null);
    setNewRow(emptyRow);
  };

  const saveEdit = () => {
    fetch(`${API_URL}/api/store-revenue/${editingId}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(editRow),
    })
      .then((r) => r.json())
      .then(() => fetchRevenue())
      .catch(() => {});
    setEditingId(null);
  };

  const deleteEntry = (id) => {
    fetch(`${API_URL}/api/store-revenue/${id}`, { method: "DELETE" })
      .then(() => fetchRevenue())
      .catch(() => {});
  };

  const handleCsvImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split(/\r?\n/).filter((l) => l.trim());
      if (lines.length < 2) return;
      const header = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/['"]/g, ""));
      const colMap = {};
      // Order matters: "net" aliases are listed before the bare platform name
      // so a header like "amazon net" doesn't get claimed by amazonRevenue.
      const aliases = isDS
        ? { month: ["month", "date", "period"], amazonNet: ["amazon net"], amazonRevenue: ["amazon"], etsyNet: ["etsy net"], etsyRevenue: ["etsy"], ttShopNet: ["tiktok net", "tt shop net", "tt net", "tiktok shop net"], ttShopRevenue: ["tiktok", "tt shop"], shiftPayout: ["shift payout", "shift"], externalJobs: ["external jobs", "external"] }
        : { month: ["month", "date", "period"], sales: ["sales", "revenue"], marketing: ["marketing", "ads", "advertising"], shipping: ["shipping"], etsyNet: ["etsy", "etsy net"], amazonNet: ["amazon", "amazon net"], ttShopNet: ["tiktok", "tt shop", "tt shop net", "tiktok shop"] };
      header.forEach((h, i) => { for (const [field, keys] of Object.entries(aliases)) { if (keys.some((k) => h.includes(k))) { colMap[field] = i; break; } } });
      const imported = [];
      for (let i = 1; i < lines.length; i++) {
        const vals = lines[i].split(",").map((v) => v.trim().replace(/['"]/g, ""));
        const get = (f) => (colMap[f] !== undefined ? vals[colMap[f]] || "" : "");
        let month = get("month");
        if (month && !month.match(/^\d{4}-\d{2}$/)) {
          const d = new Date(month);
          if (!isNaN(d)) month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
          else month = "";
        }
        if (!month) continue;
        const row = { month };
        REVENUE_FIELDS.forEach((f) => { row[f.key] = get(f.key).replace(/[$,]/g, ""); });
        imported.push(row);
      }
      if (imported.length > 0) {
        fetch(`${API_URL}/api/store-revenue/batch`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ store, entries: imported }),
        })
          .then(() => fetchRevenue())
          .catch(() => {});
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const fmtFull = (v) => `$${parseFloat(v || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ fontFamily: "'Archivo Black', 'Archivo', sans-serif", fontSize: 32, fontWeight: 900, color: "#0A0A0F", letterSpacing: "-0.02em", textTransform: "uppercase", lineHeight: 1, marginBottom: 4 }}>{storeName} — Profitability</h2>
        <p style={{ fontSize: 14, color: "rgba(10,10,15,0.58)", marginBottom: 20 }}>Revenue, expenses, and profit across all sales channels</p>
      </div>

      {/* Year selector */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20 }}>
        <button onClick={() => setSelectedYear((y) => y - 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>‹</button>
        <span style={{ fontSize: 20, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: "#0A0A0F", minWidth: 60, textAlign: "center" }}>{selectedYear}</span>
        <button onClick={() => setSelectedYear((y) => y + 1)} style={{ ...S.iconBtn, padding: "6px 10px", fontSize: 16 }}>›</button>
      </div>

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div style={S.statCard}><span style={S.statLabel}>Total Revenue</span><span style={{ ...S.statValue, color: "#0033FF" }}>{fmtFull(yearTotal)}</span></div>
        {isDS && <div style={S.statCard}><span style={S.statLabel}>Total Net</span><span style={{ ...S.statValue, color: "#10b981" }}>{fmtFull(yearNet)}</span><span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>after platform fees</span></div>}
        <div style={S.statCard}><span style={S.statLabel}>Total Expenses</span><span style={{ ...S.statValue, color: "#ef4444" }}>{fmtFull(yearExpenseTotal)}</span></div>
        <div style={S.statCard}>
          <span style={S.statLabel}>Net Profit</span>
          <span style={{ ...S.statValue, color: yearProfit >= 0 ? "#34d399" : "#ef4444" }}>{yearProfit < 0 ? `-${fmtFull(Math.abs(yearProfit))}` : fmtFull(yearProfit)}</span>
          <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)", fontFamily: "'JetBrains Mono', monospace" }}>{yearMargin.toFixed(1)}% margin</span>
        </div>
        {REVENUE_FIELDS.map((f) => (
          <div key={f.key} style={S.statCard}>
            <span style={S.statLabel}>{f.label}</span>
            <span style={{ ...S.statValue, fontSize: 20, color: FIELD_COLORS[f.key] }}>{fmtFull(yearByField[f.key])}</span>
          </div>
        ))}
        {splits.map((o, i) => {
          const share = yearProfit * (o.percentage / 100);
          return (
            <div key={o.name} style={S.statCard}>
              <span style={S.statLabel}>{o.name}'s Share</span>
              <span style={{ ...S.statValue, fontSize: 20, color: share >= 0 ? "#34d399" : "#ef4444" }}>{share < 0 ? `-${fmtFull(Math.abs(share))}` : fmtFull(share)}</span>
              <span style={{ fontSize: 12, color: OWNER_COLORS[i % OWNER_COLORS.length], fontFamily: "'JetBrains Mono', monospace" }}>{o.percentage}%</span>
            </div>
          );
        })}
      </div>

      {/* Chart */}
      <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginBottom: 24 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Monthly Revenue & Profit</h3>
        <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#0A0A0F" }}>
            <span style={{ width: 16, height: 3, background: "#0033FF", borderRadius: 0, display: "inline-block" }} /> Revenue
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#0A0A0F" }}>
            <span style={{ width: 16, height: 3, background: "#34d399", borderRadius: 0, display: "inline-block", borderTop: "1px dashed #34d399" }} /> Profit
          </div>
        </div>
        <RevenueLineChart data={monthlyData} />
      </div>

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
        <button onClick={() => fileInputRef.current && fileInputRef.current.click()} style={{ ...S.addBtn, fontSize: 13 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
          Import CSV
        </button>
        <input ref={fileInputRef} type="file" accept=".csv" onChange={handleCsvImport} style={{ display: "none" }} />
        <button onClick={() => setAddingMonth(true)} style={S.addBtn}><PlusIcon /> Add Month</button>
      </div>

      {/* Add form */}
      {addingMonth && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginBottom: 16 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px 16px" }}>
            <div>
              <label style={S.label}>Month</label>
              <input type="month" value={newRow.month} onChange={(e) => setNewRow({ ...newRow, month: e.target.value })} style={{ ...S.input, colorScheme: "dark" }} />
            </div>
            {REVENUE_FIELDS.map((f) => (
              <div key={f.key}>
                <label style={S.label}>{f.label} ($)</label>
                <input type="number" value={newRow[f.key]} onChange={(e) => setNewRow({ ...newRow, [f.key]: e.target.value })} placeholder="0.00" step="0.01" style={S.input} />
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
            <button onClick={() => setAddingMonth(null)} style={S.clearBtn}>Cancel</button>
            <button onClick={saveNew} style={S.saveJobBtn}>Save</button>
          </div>
        </div>
      )}

      {/* Monthly entries */}
      {grouped.map((entry) => {
        const total = getTotal(entry);
        const isOpen = collapsed[entry.month] !== true;
        const isEditing = editingId === entry.id;

        return (
          <div key={entry.id} style={{ marginBottom: 12 }}>
            <button onClick={() => toggle(entry.month)} style={S.monthHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ChevronIcon open={isOpen} />
                <span style={{ fontSize: 17, fontWeight: 600, color: "#0A0A0F" }}>{fmtMonth(entry.month)}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 16, fontFamily: "'JetBrains Mono', monospace", fontSize: 15 }}>
                <span style={{ color: "#0033FF", fontWeight: 700 }}>{fmtFull(total)}</span>
                {isDS && <span style={{ color: "#10b981", fontWeight: 600, fontSize: 13 }}>{fmtFull(getNet(entry))} net</span>}
                {(() => { const p = getNet(entry) - (monthlyExpenses[entry.month] || 0); return <span style={{ fontWeight: 600, color: p >= 0 ? "#34d399" : "#ef4444", fontSize: 13 }}>{p >= 0 ? "" : "-"}{fmtFull(Math.abs(p))} profit</span>; })()}
              </div>
            </button>
            {isOpen && (
              <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderTop: "none", borderRadius: 0, padding: "16px 20px" }}>
                {isEditing ? (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "10px 16px" }}>
                      {REVENUE_FIELDS.map((f) => (
                        <div key={f.key}>
                          <label style={S.label}>{f.label} ($)</label>
                          <input type="number" value={editRow[f.key] || ""} onChange={(e) => setEditRow({ ...editRow, [f.key]: e.target.value })} placeholder="0.00" step="0.01" style={S.input} />
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 12, justifyContent: "flex-end" }}>
                      <button onClick={() => setEditingId(null)} style={S.clearBtn}>Cancel</button>
                      <button onClick={saveEdit} style={{ ...S.iconBtn, borderColor: "#22c55e", color: "#22c55e", padding: "6px 14px" }}><CheckIcon /></button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px 24px", marginBottom: 16 }}>
                      {REVENUE_FIELDS.map((f) => (
                        <div key={f.key}>
                          <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>{f.label}</div>
                          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: FIELD_COLORS[f.key] }}>{fmtFull(entry[f.key])}</div>
                        </div>
                      ))}
                    </div>
                    {/* Profitability summary */}
                    {(() => {
                      const expAmt = monthlyExpenses[entry.month] || 0;
                      const net = getNet(entry);
                      const profit = net - expAmt;
                      const margin = total > 0 ? (profit / total) * 100 : 0;
                      return (
                        <div style={{ display: "grid", gridTemplateColumns: isDS ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap: "12px 24px", paddingTop: 12, marginBottom: 16, borderTop: "2px solid #0A0A0F" }}>
                          {isDS && (
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>Total Net</div>
                              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "#10b981" }}>{fmtFull(net)}</div>
                            </div>
                          )}
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>Expenses</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: "#ef4444" }}>{fmtFull(expAmt)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>Profit</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: profit >= 0 ? "#34d399" : "#ef4444" }}>{profit < 0 ? `-${fmtFull(Math.abs(profit))}` : fmtFull(profit)}</div>
                          </div>
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 4 }}>Margin</div>
                            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 18, fontWeight: 700, color: profit >= 0 ? "#34d399" : "#ef4444" }}>{margin.toFixed(1)}%</div>
                          </div>
                        </div>
                      );
                    })()}
                    {splits.length > 0 && (() => {
                      const expAmt = monthlyExpenses[entry.month] || 0;
                      const mProfit = getNet(entry) - expAmt;
                      return (
                        <div style={{ paddingTop: 8, marginBottom: 16, borderTop: "2px solid #0A0A0F" }}>
                          <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 8 }}>Owner Distributions</div>
                          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                            {splits.map((o, i) => {
                              const share = mProfit * (o.percentage / 100);
                              return (
                                <div key={o.name} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  <span style={{ width: 8, height: 8, borderRadius: 0, background: OWNER_COLORS[i % OWNER_COLORS.length], flexShrink: 0 }} />
                                  <span style={{ fontSize: 13, color: "#0A0A0F" }}>{o.name}</span>
                                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, color: share >= 0 ? "#34d399" : "#ef4444" }}>{share < 0 ? `-${fmtFull(Math.abs(share))}` : fmtFull(share)}</span>
                                  <span style={{ fontSize: 12, color: "rgba(10,10,15,0.58)" }}>{o.percentage}%</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "rgba(10,10,15,0.58)" }}>
                        Revenue: <span style={{ color: "#0033FF", fontWeight: 700 }}>{fmtFull(total)}</span>
                        {isDS && <> · Net: <span style={{ color: "#10b981", fontWeight: 700 }}>{fmtFull(getNet(entry))}</span></>}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => { setEditingId(entry.id); setEditRow({ ...entry }); }} style={S.iconBtn} title="Edit"><EditIcon /></button>
                        <button onClick={() => deleteEntry(entry.id)} style={{ ...S.iconBtn, borderColor: "#ef4444", color: "#ef4444" }} title="Delete"><TrashIcon /></button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {yearEntries.length === 0 && !addingMonth && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 40, textAlign: "center", color: "rgba(10,10,15,0.58)" }}>
          No revenue data for {selectedYear}. Click "Add Month" or import a CSV.
        </div>
      )}

      {/* Revenue breakdown */}
      {yearTotal > 0 && (
        <div style={{ background: "#FFFFFF", border: "2px solid #0A0A0F", borderRadius: 0, padding: 20, marginTop: 24 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.5, color: "rgba(10,10,15,0.58)", marginBottom: 14 }}>{selectedYear} Revenue by Channel</h3>
          <div style={{ display: "flex", height: 32, borderRadius: 0, overflow: "hidden", marginBottom: 16 }}>
            {GROSS_FIELDS.map((f) => {
              const val = yearByField[f.key] || 0;
              return <div key={f.key} style={{ width: `${(val / yearTotal) * 100}%`, background: FIELD_COLORS[f.key], transition: "width .4s", minWidth: val > 0 ? 2 : 0 }} title={`${f.label}: ${fmtFull(val)}`} />;
            })}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {GROSS_FIELDS.map((f) => {
              const val = yearByField[f.key] || 0;
              const pct = yearTotal > 0 ? (val / yearTotal) * 100 : 0;
              return (
                <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: isDS ? 170 : 120 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 0, background: FIELD_COLORS[f.key], flexShrink: 0 }} />
                    <span style={{ fontSize: 14, color: "#0A0A0F" }}>{f.label}</span>
                  </div>
                  <div style={{ flex: 1, height: 6, background: "#FFFFFF", borderRadius: 0, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${pct}%`, background: FIELD_COLORS[f.key], borderRadius: 0, transition: "width .3s" }} />
                  </div>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#0A0A0F", fontWeight: 600, minWidth: 80, textAlign: "right" }}>{fmtFull(val)}</span>
                  {isDS && <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#10b981", fontWeight: 600, minWidth: 100, textAlign: "right" }}>{fmtFull(yearByField[f.netKey])} net</span>}
                  <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", minWidth: 40, textAlign: "right" }}>{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
          {/* Profit summary */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: "2px solid #0A0A0F", display: "grid", gridTemplateColumns: isDS ? "1fr 1fr 1fr 1fr" : "1fr 1fr 1fr", gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 6 }}>Total Revenue</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "#0033FF" }}>{fmtFull(yearTotal)}</div>
            </div>
            {isDS && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 6 }}>Total Net</div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "#10b981" }}>{fmtFull(yearNet)}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 6 }}>Total Expenses</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: "#ef4444" }}>{fmtFull(yearExpenseTotal)}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 6 }}>Net Profit ({yearMargin.toFixed(1)}% margin)</div>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 20, fontWeight: 700, color: yearProfit >= 0 ? "#34d399" : "#ef4444" }}>{yearProfit < 0 ? `-${fmtFull(Math.abs(yearProfit))}` : fmtFull(yearProfit)}</div>
            </div>
          </div>
          {splits.length > 0 && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "2px solid #0A0A0F" }}>
              <div style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1.2, color: "rgba(10,10,15,0.58)", marginBottom: 12 }}>Owner Distributions</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {splits.map((o, i) => {
                  const share = yearProfit * (o.percentage / 100);
                  const absShare = Math.abs(share);
                  const maxShare = Math.max(...splits.map((s) => Math.abs(yearProfit * (s.percentage / 100))), 1);
                  const barPct = (absShare / maxShare) * 100;
                  const color = OWNER_COLORS[i % OWNER_COLORS.length];
                  return (
                    <div key={o.name} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 120 }}>
                        <span style={{ width: 10, height: 10, borderRadius: 0, background: color, flexShrink: 0 }} />
                        <span style={{ fontSize: 14, color: "#0A0A0F" }}>{o.name}</span>
                      </div>
                      <div style={{ flex: 1, height: 6, background: "#FFFFFF", borderRadius: 0, overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${barPct}%`, background: color, borderRadius: 0, transition: "width .3s" }} />
                      </div>
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 600, minWidth: 80, textAlign: "right", color: share >= 0 ? "#34d399" : "#ef4444" }}>{share < 0 ? `-${fmtFull(absShare)}` : fmtFull(absShare)}</span>
                      <span style={{ fontSize: 13, color: "rgba(10,10,15,0.58)", minWidth: 40, textAlign: "right" }}>{o.percentage}%</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
